import React from "react";
import { useLocation } from "react-router-dom";
import ROUTES from "../seo-routes.json";

const DEFAULT_DESCRIPTION = "See the business behind the stock with India-focused company research and financial intelligence from LONGVIEW.";

const setMeta = (name, content, attribute = "name") => {
  let element = document.head.querySelector(`meta[${attribute}="${name}"]`);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, name);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
};

const cleanBaseUrl = () => {
  const configured = (process.env.REACT_APP_SITE_URL || "").trim();
  const fallback = typeof window !== "undefined" ? window.location.origin : "";
  const candidate = (configured || fallback).replace(/\/$/, "");
  return /^https?:\/\//i.test(candidate) ? candidate : "";
};

const metadataFor = (pathname) => {
  if (ROUTES[pathname]) return ROUTES[pathname];
  if (pathname.startsWith("/company/")) {
    const ticker = decodeURIComponent(pathname.split("/")[2] || "").toUpperCase();
    return {
      title: `${ticker || "Company"} Company Research | LONGVIEW`,
      description: `Explore ${ticker || "this company"} through the company, market, event, and research context currently available in LONGVIEW.`,
      indexable: true,
    };
  }
  if (pathname.startsWith("/learn/")) {
    return { title: "Investing Lesson | LONGVIEW", description: "Read an accessible LONGVIEW lesson about investing, finance, and company research.", indexable: true };
  }
  return { title: "Page Not Found | LONGVIEW", description: DEFAULT_DESCRIPTION, indexable: false };
};

export const RouteMetadata = () => {
  const { pathname } = useLocation();

  React.useEffect(() => {
    const normalizedPath = pathname !== "/" ? pathname.replace(/\/$/, "") : pathname;
    const { title, description, indexable } = metadataFor(normalizedPath);
    const baseUrl = cleanBaseUrl();
    const canonicalUrl = baseUrl ? `${baseUrl}${normalizedPath === "/" ? "/" : normalizedPath}` : "";

    document.title = title;
    setMeta("description", description);
    setMeta("robots", indexable ? "index,follow" : "noindex,follow");
    setMeta("og:type", "website", "property");
    setMeta("og:site_name", "LONGVIEW", "property");
    setMeta("og:title", title, "property");
    setMeta("og:description", description, "property");
    setMeta("twitter:card", "summary");
    setMeta("twitter:title", title);
    setMeta("twitter:description", description);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (canonicalUrl) {
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.setAttribute("rel", "canonical");
        document.head.appendChild(canonical);
      }
      canonical.setAttribute("href", canonicalUrl);
      setMeta("og:url", canonicalUrl, "property");
    } else {
      canonical?.remove();
      document.head.querySelector('meta[property="og:url"]')?.remove();
    }
  }, [pathname]);

  return null;
};
